/**
 * CollectionCritiqueModePage - Page component for critique session mode
 * Feature: 032-critique-detail-view, 055-my-critiques-page
 *
 * Route: /crit/:id/:slug? where :id is a session ID (cs prefix)
 * Requires authentication
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link } from '@/components/atoms/Link';
import { CritiqueModeFooterControls, type LayoutMode } from '../components/composed/CritiqueModeFooterControls/CritiqueModeFooterControls';
import { CritiqueModeHeader } from '../components/composed/CritiqueModeHeader/CritiqueModeHeader';
import { CritiqueModeBody } from '../components/composed/CritiqueModeBody/CritiqueModeBody';
import type { ZoomLevel } from '../hooks/useZoomPan';
import { useCritiqueModeHandlers } from '../hooks/useCritiqueModeHandlers';
import { useCritiqueModeKeyboard } from '../hooks/useCritiqueModeKeyboard';
import { PageHeader, PageHeaderLabel, PageHeaderCommandWell } from '../components/PageHeader';
import { useCollectionGraphQL } from '../graphql/hooks/useCollectionsGraphQL';
import { useCollectionPhotosCritiquesGraphQL, useCritiqueSessionGraphQL } from '../graphql/hooks/useCritiquesGraphQL';
import { useCritiqueUrlState } from '../hooks/useCritiqueUrlState';
import { useScrollToCritique } from '../utils/critiqueScroll';
import { isValidGlobalId, collectionEditUrl, userProfileUrl, critiqueSessionUrl } from '../utils/urls';
import {
  useUpdateCritiqueSessionGraphQL,
  useDeleteCritiqueSessionGraphQL
} from '../hooks/graphql/useCritiqueMutationsGraphQL';
import { useCompleteViewPhotoTaskGraphQL } from '../hooks/graphql/useTaskMutationsGraphQL';
import { useResumeAICritiqueSessionGraphQL } from '../hooks/graphql/useCollectionMutationsGraphQL';
import { useAuth } from '../services/auth';
import { useViewMode } from '../contexts/ViewModeContext';
import { useFooterContent } from '../contexts/FooterContext';
import logger from '@/utils/logger';
import { CollectionIcon } from '../components/icons';
import { KeyboardShortcutsModal } from '../components/modals/KeyboardShortcutsModal';
import { CritiqueSessionSettingsDialog } from '../components/composed/CritiqueSessionSettingsDialog';
import { ShareDialog } from '../components/ShareDialog';
import type { PhotoItem2ViewModel } from '../components/PhotoItem2/PhotoItem2.types';
import type { FeedbackType, NCommentsConfig } from '../types/photo-rating';

// View mode configurations (moved outside component for performance)
const VIEW_MODE_CONFIGURATIONS = {
  list: {
    displayName: "List",
    collectionPosition: "left" as const,
    layout: 'vertical' as const,
    itemTemplate: 'classic-feed' as const,
    hideDetail: false,
    zoom: 1,
    defaultCollectionSize: 30,
  },
  lightroomClone: {
    displayName: "Lightroom List",
    collectionPosition: "bottom" as const,
    layout: 'horizontal' as const,
    itemTemplate: 'lightroom-list' as const,
    hideDetail: false,
    zoom: .9,
    defaultCollectionSize: 10,
  },
  grid: {
    displayName: "Grid",
    collectionPosition: "left" as const,
    layout: 'grid' as const,
    itemTemplate: 'full-details' as const,
    hideDetail: true,
    zoom: 1.2,
    defaultCollectionSize: 40,
  },
  magazine: {
    displayName: "Magazine",
    collectionPosition: "left" as const,
    layout: 'magazine' as const,
    itemTemplate: 'classic-feed' as const,
    hideDetail: true,
    zoom: 2,
    defaultCollectionSize: 50,
  },
} as const;

export function CollectionCritiqueModePage() {
  const { username, slug, id: routeId } = useParams<{
    username?: string;
    slug?: string;
    id?: string;
  }>();

  const navigate = useNavigate();
  const { user } = useAuth();

  // Mutations for session editing and deletion (Feature 065: Migrated to GraphQL)
  const { updateCritiqueSession } = useUpdateCritiqueSessionGraphQL();
  const { deleteCritiqueSession, loading: deleteLoading } = useDeleteCritiqueSessionGraphQL();
  // Feature 071 Phase 10: Resume paused AI critique session
  const { resumeAICritiqueSession, loading: resumeLoading } = useResumeAICritiqueSessionGraphQL();
  // Feature 651: Auto-complete VIEW_PHOTO tasks when viewing photos
  const { completeViewPhotoTask } = useCompleteViewPhotoTaskGraphQL();

  // Fetch session data
  // Feature 063: Migrated to GraphQL
  // Feature 071: Poll for AI feedback status updates while pending > 0
  const sessionQuery = useCritiqueSessionGraphQL(routeId);
  const session = sessionQuery.data;

  // Feature 071: Determine if AI feedback is in progress
  const isAiFeedbackPending = session?.isAiSession && (session?.aiFeedbackStatus?.pending ?? 0) > 0;

  // Feature 071: Track polling state to avoid dependency on unstable function references
  const sessionPollingRef = useRef<{ startPolling: (interval: number) => void; stopPolling: () => void } | null>(null);
  sessionPollingRef.current = { startPolling: sessionQuery.startPolling, stopPolling: sessionQuery.stopPolling };

  // Feature 071: Start/stop polling based on AI feedback status
  useEffect(() => {
    if (isAiFeedbackPending) {
      logger.info('AI', 'Starting session polling for AI feedback updates');
      sessionPollingRef.current?.startPolling(3000); // Poll every 3 seconds
    } else {
      sessionPollingRef.current?.stopPolling();
    }
    // Cleanup on unmount
    return () => {
      sessionPollingRef.current?.stopPolling();
    };
  }, [isAiFeedbackPending]);

  // Get collection ID from session data
  const effectiveCollectionId = session?.collectionId;

  // Validate global ID format for early return decision (checked after all hooks)
  const isInvalidGlobalId = routeId && !isValidGlobalId(routeId);

  // Fetch collection data using GraphQL hook
  // Feature 063: Migrated to GraphQL - useCollectionGraphQL returns both collection and items
  const { data: collection, items: collectionItems, isLoading: collectionLoading, error: collectionError } = useCollectionGraphQL(effectiveCollectionId);

  // Update URL bar to include slug if not provided
  useEffect(() => {
    if (routeId && !slug && session) {
      window.history.replaceState(null, '', critiqueSessionUrl(routeId, session.slug));
    }
  }, [routeId, slug, session]);

  const items = collectionItems;

  // Extract photo IDs for batch critique fetching
  // Updated to use photoData.id after API refactor to PhotoItem2ViewModel format
  const photoIds = items?.map((item: PhotoItem2ViewModel) => item.photoData?.id).filter(Boolean) || [];

  // Fetch top-level critiques for all photos in the collection (032-critique-detail-view)
  // Feature 056 US2: Filter critiques by session when viewing a specific session
  // Feature 063: Migrated to GraphQL
  // Feature 071: Poll for new AI critiques while feedback is pending
  const critiquesQuery = useCollectionPhotosCritiquesGraphQL(
    collection?.globalId,
    photoIds,
    5, // Fetch up to 5 top-level critiques per photo for the feed
    session?.globalId // Filter by session ID when viewing a specific session
  );
  const { critiquesMap, isLoading: critiquesLoading } = critiquesQuery;

  // Feature 071: Track critiques polling state to avoid dependency on unstable function references
  const critiquesPollingRef = useRef<{ startPolling: (interval: number) => void; stopPolling: () => void } | null>(null);
  critiquesPollingRef.current = { startPolling: critiquesQuery.startPolling, stopPolling: critiquesQuery.stopPolling };

  // Feature 071: Start/stop critiques polling based on AI feedback status
  useEffect(() => {
    if (isAiFeedbackPending) {
      logger.info('AI', 'Starting critiques polling for new AI feedback');
      critiquesPollingRef.current?.startPolling(3000); // Poll every 3 seconds
    } else {
      critiquesPollingRef.current?.stopPolling();
    }
    // Cleanup on unmount
    return () => {
      critiquesPollingRef.current?.stopPolling();
    };
  }, [isAiFeedbackPending]);

  // Transform collection items to PhotoItem2ViewModel format, including critiques (T009)
  // Merge critiques from critiquesMap into each photo item
  const photos = items?.map((item: PhotoItem2ViewModel) => ({
    ...item,
    critiques: critiquesMap[item.photoData?.id] || []
  })) || [];

  // T011: URL state management for photo/comment selection (040-critique-url-state)
  // Pass photos array instead of photoIds for proper validation
  const { state, selectPhoto, selectComment } = useCritiqueUrlState(
    collection?.globalId || '',
    photos
  );

  // T012: Derive selectedIds from URL state (040-critique-url-state)
  const selectedIds = state.photoId ? [state.photoId] : [];

  // Feature 651: Wrapper for photo selection that also completes VIEW_PHOTO tasks
  const handlePhotoSelect = useCallback((photoId: string) => {
    selectPhoto(photoId);
    // Auto-complete VIEW_PHOTO task if session exists
    if (session?.globalId) {
      completeViewPhotoTask(session.globalId, photoId).catch(() => {
        // Silent failure - task may not exist or already be complete
      });
    }
  }, [selectPhoto, session?.globalId, completeViewPhotoTask]);

  // T024: Auto-scroll to selected critique when URL contains comment ID (040-critique-url-state)
  useScrollToCritique(state.commentId);

  // T012: Auto-select first photo if no photo in URL (040-critique-url-state)
  // Feature 651: Uses handlePhotoSelect to also trigger VIEW_PHOTO task completion
  useEffect(() => {
    if (photos.length > 0 && !state.photoId && photos[0]?.photoData?.id) {
      handlePhotoSelect(photos[0].photoData.id);
    }
  }, [photos.length, state.photoId, handlePhotoSelect, photos]);

  // Bug fix: Complete VIEW_PHOTO task when navigating directly with photo ID in URL
  // (e.g., via "Go to Task" link). The auto-select effect above only handles when
  // NO photo ID is in the URL. This effect handles the case where a photo ID is
  // already present in the URL when the page loads.
  useEffect(() => {
    if (session?.globalId && state.photoId) {
      completeViewPhotoTask(session.globalId, state.photoId).catch(() => {
        // Silent failure - task may not exist or already be complete
      });
    }
  }, [session?.globalId, state.photoId, completeViewPhotoTask]);

  // Reply context state - lifted to page level to persist across modals
  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  // Layout mode state for CollectionAndDetailView2 layout (renamed from viewMode to avoid collision)
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('list');

  // Zoom level state - persists within session, resets on unmount (Feature 646-critique-zoom-control)
  const [sessionZoomLevel, setSessionZoomLevelInternal] = useState<ZoomLevel>('fit');

  // T057: Wrapper with structured logging for zoom level changes (Feature 646)
  const setSessionZoomLevel = useCallback((newLevel: ZoomLevel) => {
    logger.info('ZoomControl', 'Zoom level changed', { from: sessionZoomLevel, to: newLevel });
    setSessionZoomLevelInternal(newLevel);
  }, [sessionZoomLevel]);

  // Lighting/fullscreen view mode from context (Feature 057-lightbox-fullscreen-oneup)
  const { viewMode, setViewMode } = useViewMode();

  // Footer content publishing (Feature 072: unified footer)
  const { setRightContent, clearContent } = useFooterContent();

  // Layout mode icons - memoized to prevent unnecessary effect re-runs
  const layoutOptions = useMemo(() => [
    {
      value: 'list',
      label: VIEW_MODE_CONFIGURATIONS.list.displayName,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      ),
    },
    {
      value: 'lightroomClone',
      label: VIEW_MODE_CONFIGURATIONS.lightroomClone.displayName,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      value: 'grid',
      label: VIEW_MODE_CONFIGURATIONS.grid.displayName,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
        </svg>
      ),
    },
    {
      value: 'magazine',
      label: VIEW_MODE_CONFIGURATIONS.magazine.displayName,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      ),
    },
  ], []);

  // Reset viewMode to normal when leaving the page (Feature 057-lightbox-fullscreen-oneup)
  useEffect(() => {
    return () => {
      setViewMode('normal');
    };
  }, [setViewMode]);

  // Keyboard shortcuts modal state (Feature 057-lightbox-fullscreen-oneup)
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  // Feature 649: Session settings dialog state
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Feature 080: Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Feature 649/651: Handler for saving session settings (feedback options + n_comments config)
  const handleSaveSettings = useCallback(async (feedbackOptions: FeedbackType[], nCommentsConfig?: NCommentsConfig) => {
    if (!session?.globalId) return;

    setSettingsSaving(true);
    try {
      await updateCritiqueSession({
        id: session.globalId,
        feedbackOptions,
        nCommentsConfig,
      });
      logger.info('Settings', 'Session feedback options updated', { sessionId: session.globalId, feedbackOptions, nCommentsConfig });
      setShowSettingsDialog(false);
      // Refetch session to get updated data
      sessionQuery.refetch();
    } catch (error) {
      logger.error('Settings', 'Failed to update session settings', { error });
    } finally {
      setSettingsSaving(false);
    }
  }, [session?.globalId, updateCritiqueSession, sessionQuery]);

  // Event handlers hook - consolidates all handler logic
  const {
    handleTitleChange,
    handleDescriptionChange,
    handleDeleteSession,
    handleResumeSession,
    handleCritiqueClick,
    handleCommentClick,
    handleCommentSubmitted,
    toggleDimmedLights,
    toggleFullscreen,
    exitViewMode,
    isSessionOwner,
    isCollectionOwner,
  } = useCritiqueModeHandlers({
    session,
    collection,
    photos,
    user,
    urlState: state,
    selectPhoto,
    selectComment,
    navigate,
    setViewMode,
    updateCritiqueSession,
    deleteCritiqueSession,
    resumeAICritiqueSession,
  });

  // Scroll to selected photo when photoId changes (critique card click navigation)
  useEffect(() => {
    if (state.photoId) {
      // Small delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        const photoElement = document.querySelector(`[data-photo-id="${state.photoId}"]`);
        if (photoElement) {
          photoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [state.photoId]);

  // Publish layout selector, zoom control, and view mode controls to footer right well
  useEffect(() => {
    setRightContent(
      <CritiqueModeFooterControls
        layoutMode={layoutMode}
        layoutOptions={layoutOptions}
        sessionZoomLevel={sessionZoomLevel}
        viewMode={viewMode}
        onLayoutChange={setLayoutMode}
        onZoomChange={setSessionZoomLevel}
        onToggleDimmedLights={toggleDimmedLights}
        onToggleFullscreen={toggleFullscreen}
      />
    );

    return () => clearContent();
  }, [layoutMode, layoutOptions, sessionZoomLevel, viewMode, toggleDimmedLights, toggleFullscreen, setRightContent, clearContent]);

  // Keyboard shortcuts hook - consolidates all shortcut registrations
  // Feature 651: Uses handlePhotoSelect to also trigger VIEW_PHOTO task completion
  useCritiqueModeKeyboard({
    photos,
    urlState: state,
    viewMode,
    sessionZoomLevel,
    selectPhoto: handlePhotoSelect,
    setShowKeyboardShortcuts,
    setSessionZoomLevel,
    toggleDimmedLights,
    toggleFullscreen,
    exitViewMode,
  });

  // Early return for invalid global ID format - must be after all hooks
  if (isInvalidGlobalId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-danger mb-4">Invalid ID format</p>
        </div>
      </div>
    );
  }

  // Loading state
  // Feature 063: Simplified - useCollectionGraphQL returns items in same query
  const isLoading = collectionLoading || critiquesLoading || sessionQuery.isLoading;

  // T014: Ensure loading state displays correctly
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted">Loading collection...</p>
      </div>
    );
  }

  // Handle session errors
  if (sessionQuery.error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-muted mb-4">Discussion not found</p>
          <Link to="/disc" className="text-primary hover:underline">
            View all discussions
          </Link>
        </div>
      </div>
    );
  }

  // T015: Ensure error state displays correctly
  if (collectionError || (!isLoading && !collection)) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-muted mb-4">Collection not found</p>
          {username && (
            <Link to={userProfileUrl(username)} className="text-primary hover:underline">
              View {username}'s collections
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (!collection) {
    return null; // Still loading, will show loading state above
  }

  // T016: Handle empty collection state
  if (photos.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader>
          <PageHeaderLabel
            title={collection.name}
            subtitle={collection.description}
          />
          <PageHeaderCommandWell>
            <Link
              to={collectionEditUrl(collection.globalId!, collection.slug)}
              variant="ghost"
              size="md"
              className="flex items-center gap-2"
            >
              <CollectionIcon size={16} />
              Edit
            </Link>
          </PageHeaderCommandWell>
        </PageHeader>
        <div className="flex items-center justify-center flex-1">
          <p className="text-muted">No photos in this collection yet</p>
        </div>
      </div>
    );
  }

  // Find selected photo data for critique panel
  const selectedPhoto = selectedIds[0] ? photos.find((p) => p.photoData.id === selectedIds[0]) : undefined;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <CritiqueModeHeader
        session={session}
        collection={collection}
        isSessionOwner={isSessionOwner}
        isCollectionOwner={isCollectionOwner}
        resumeLoading={resumeLoading}
        deleteLoading={deleteLoading}
        onTitleChange={handleTitleChange}
        onDescriptionChange={handleDescriptionChange}
        onDeleteSession={handleDeleteSession}
        onResumeSession={handleResumeSession}
        onSettingsClick={() => setShowSettingsDialog(true)}
        onShareClick={() => setShowShareDialog(true)}
      />

      {/* Main content - Horizontal split: Collection+Detail view on left, Comments on right */}
      <CritiqueModeBody
        photos={photos}
        selectedPhoto={selectedPhoto}
        layoutMode={layoutMode}
        viewModeConfig={VIEW_MODE_CONFIGURATIONS[layoutMode]}
        viewMode={viewMode}
        urlState={state}
        selectedIds={selectedIds}
        session={session}
        collection={collection}
        replyingTo={replyingTo}
        setReplyingTo={setReplyingTo}
        sessionZoomLevel={sessionZoomLevel}
        setSessionZoomLevel={setSessionZoomLevel}
        isSessionOwner={isSessionOwner}
        isCollectionOwner={isCollectionOwner}
        onPhotoSelect={handlePhotoSelect}
        onCritiqueClick={handleCritiqueClick}
        onCommentClick={handleCommentClick}
        onCommentSubmitted={handleCommentSubmitted}
      />

      {/* Keyboard shortcuts help modal (Feature 057-lightbox-fullscreen-oneup) */}
      <KeyboardShortcutsModal
        open={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      {/* Feature 649/651: Session settings dialog for feedback options + N Comments */}
      {session && (
        <CritiqueSessionSettingsDialog
          open={showSettingsDialog}
          onClose={() => setShowSettingsDialog(false)}
          onSave={handleSaveSettings}
          sessionTitle={session.title}
          currentFeedbackOptions={(session.feedbackOptions?.enabled || ['general']) as FeedbackType[]}
          currentNCommentsConfig={session.nCommentsTargetCount ? { targetCount: session.nCommentsTargetCount } : undefined}
          photoCount={photos.length}
          isSaving={settingsSaving}
        />
      )}

      {/* Feature 080: Share dialog for collection owners */}
      {session && (
        <ShareDialog
          open={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          critiqueSessionId={session.globalId}
          collectionSlug={collection.slug}
          title={session.title}
        />
      )}
    </div>
  );
}
